
  import { Component, Prop, Vue } from "vue-property-decorator";
  import GET_DOCUMENT_ATTACHMENTS from "@/apollo/documents/GetDocumentAttachments.gql";
  import { Document, Document_Project, Document_User, User } from "@/generated/graphql";
  import getFullName from "@/helpers/getFullName";
  import RouteNames from "@/entities/RouteNames";
  import Roles from "@/entities/Roles";
  import DocumentAttachedTo from "@/entities/document/DocumentAttachedTo";
  import DocumentAttachedToDetails from "@/entities/document/DocumentAttachedToDetails";

  @Component({
    apollo: {
      documentDetails: {
        query: GET_DOCUMENT_ATTACHMENTS,
        variables() {
          return {
            documentId: this.document.id,
            managerId: this.currentUser.manager?.id,
          };
        },
        update(data) {
          return data.document_by_pk;
        },
      },
    },
  })
  export default class ManagementDocumentInfoProvider extends Vue {
    @Prop({ type: Object, required: true }) readonly document!: Document;
    @Prop({ type: Object, required: true }) readonly currentUser!: User;

    documentDetails: Document | null = null;

    get processing(): boolean {
      return this.$apollo.queries.documentDetails.loading;
    }

    get group(): DocumentAttachedTo {
      const title = "group";
      const team = this.documentDetails!.team;

      if (!team) {
        return {
          id: title,
          details: [],
        };
      }

      const routeParams = {
        name: RouteNames.MANAGE_TEAMS,
        params: {
          id: team.id,
        },
      };

      return {
        id: team.id,
        title,
        details: [{
          value: team.name!,
          routeParams,
        }],
      };
    }

    get project(): DocumentAttachedTo {
      const title = "Client";
      return {
        id: title,
        title: "Client",
        details: this.documentDetails!.document_projects.map<DocumentAttachedToDetails>(({ project }: Document_Project) => ({
          value: project.client_name!,
          routeParams: {
            name: RouteNames.PROJECT_DETAILS,
            params: {
              id: project.id,
            },
          },
        })),
      };
    }

    get manager(): DocumentAttachedTo {
      const title = "manager";
      return {
        id: title,
        title: title,
        details: this.documentDetails!.attached_to
          .filter(({ user }: Document_User) => user.role === Roles.MANAGER)
          .map(({ user }: Document_User) => {
            return {
              value: getFullName(user.given_name!, user.family_name!),
              routeParams: {
                name: RouteNames.MANAGER_ABOUT,
                params: {
                  id: user.manager!.id,
                },
              },
            };
          }),
      };
    }

    get contractor(): DocumentAttachedTo {
      const title = "contractor";
      return {
        id: title,
        title,
        details: this.documentDetails!.attached_to
          .filter(({ user }: Document_User) => user.role === Roles.CONTRACTOR)
          .map(({ user }: Document_User) => {
            const value = getFullName(user.given_name!, user.family_name!);

            if (user.contracts.length > 0) {
              return {
                value,
                routeParams: {
                  name: RouteNames.CONTRACTOR_DETAILS_DOCUMENTS,
                  params: {
                    id: user.contracts[0].id,
                  },
                },
              };
            }

            return {
              value,
              routeParams: {
                name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
                params: {
                  id: user.id,
                },
              },
            };
          }),
      };
    }

    get attachedToEntities(): DocumentAttachedTo[] {
      if (this.processing) {
        return [];
      }

      return [
        this.group,
        this.project,
        this.manager,
        this.contractor,
      ].filter(entity => entity.details.length > 0);
    }
  }
